import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Mic, MessageCircle, Gamepad2, Briefcase, ArrowRight, Sparkles, Brain, TrendingUp, Zap, RefreshCw } from 'lucide-react';
import AnimatedNumber from '@/components/premium/AnimatedNumber';
import StatChip from '@/components/premium/StatChip';
import { useT } from '@/lib/i18n';

/**
 * Interactive Demo — try HighlightAI without signing up.
 *
 * The visitor picks a persona → a scripted "processing" flow plays →
 * reveals the same UI the real app shows post-processing (AI Coach card,
 * Growth KPIs, Creator DNA mini-radar, 3 generated clip tiles).
 *
 * Everything is scripted DOM; no network calls. This keeps the section
 * ISOLATED from the backend per the sprint spec.
 */

const ACC = '#D4FF00';

const DEMOS = {
  podcast: {
    icon: Mic,
    label: 'Podcast',
    labelFr: 'Podcast',
    source: 'Marketing Growth · 58:24',
    dna: { hook: 88, retention: 74, emotion: 82, virality: 91, publishing: 60 },
    growth: { clips: 12, published: 8, views: 42800, avgScore: 87 },
    coachTip: 'Passe de 45s à 25s sur tes intros — ton Hook Score gagne +14 pts en moyenne.',
    clips: [
      { title: 'The scaling myth', score: 96, dur: 42 },
      { title: '"Do this in your first 90 days"', score: 91, dur: 38 },
      { title: 'Why founders burn out', score: 88, dur: 27 },
    ],
  },
  interview: {
    icon: MessageCircle,
    label: 'Interview',
    labelFr: 'Interview',
    source: 'Ex-Netflix VP · 41:07',
    dna: { hook: 82, retention: 79, emotion: 87, virality: 84, publishing: 55 },
    growth: { clips: 9, published: 5, views: 28100, avgScore: 82 },
    coachTip: 'Tes clips à charge émotionnelle performent 2× mieux — priorise-les.',
    clips: [
      { title: 'The moment I got fired', score: 94, dur: 33 },
      { title: '"Never tell your team that"', score: 89, dur: 41 },
      { title: 'A CEO\'s biggest regret', score: 85, dur: 28 },
    ],
  },
  gaming: {
    icon: Gamepad2,
    label: 'Gaming',
    labelFr: 'Gaming',
    source: 'Ranked Stream · 2:12:33',
    dna: { hook: 91, retention: 68, emotion: 74, virality: 93, publishing: 72 },
    growth: { clips: 18, published: 14, views: 118400, avgScore: 89 },
    coachTip: 'Publie 3× / semaine sur TikTok — c\'est ton canal le plus rentable.',
    clips: [
      { title: '1v5 CLUTCH — no way', score: 98, dur: 22 },
      { title: 'The play of the year', score: 94, dur: 31 },
      { title: 'How did that even happen', score: 88, dur: 19 },
    ],
  },
  business: {
    icon: Briefcase,
    label: 'Business',
    labelFr: 'Business',
    source: 'Q3 Earnings Recap · 32:18',
    dna: { hook: 78, retention: 82, emotion: 68, virality: 76, publishing: 65 },
    growth: { clips: 7, published: 6, views: 15200, avgScore: 79 },
    coachTip: 'Ton audience LinkedIn engage 3× plus sur les clips de 45s vs 60s.',
    clips: [
      { title: 'Our biggest hiring mistake', score: 88, dur: 44 },
      { title: '"Don\'t optimize for that"', score: 84, dur: 38 },
      { title: 'The metric nobody tracks', score: 82, dur: 51 },
    ],
  },
};

const SCRIPT_STAGES = [
  { label: 'Uploading',            labelFr: 'Upload',                     progress: 12 },
  { label: 'Extracting audio',     labelFr: 'Extraction audio',            progress: 24 },
  { label: 'Whisper transcribing', labelFr: 'Transcription Whisper',       progress: 42 },
  { label: 'Detecting hooks',      labelFr: 'Détection des hooks',         progress: 58 },
  { label: 'Scoring viral moments',labelFr: 'Scoring viralité',            progress: 72 },
  { label: 'Cutting & captioning', labelFr: 'Découpe & sous-titres',       progress: 86 },
  { label: 'Preparing AI Coach',   labelFr: 'Préparation du Coach IA',     progress: 96 },
  { label: 'Ready',                labelFr: 'Prêt',                        progress: 100 },
];

const STAGE_MS = 700;

const InteractiveDemo = () => {
  const { lang } = useT();
  const [active, setActive] = useState('podcast');
  const [stage, setStage] = useState(-1); // -1 = idle
  const [done, setDone] = useState(false);
  const timerRef = useRef();
  const demo = DEMOS[active];

  const start = () => {
    clearInterval(timerRef.current);
    setStage(0); setDone(false);
    timerRef.current = setInterval(() => {
      setStage((s) => {
        if (s + 1 >= SCRIPT_STAGES.length) {
          clearInterval(timerRef.current);
          setDone(true);
          return s;
        }
        return s + 1;
      });
    }, STAGE_MS);
  };

  const restart = () => {
    setStage(-1); setDone(false);
    clearInterval(timerRef.current);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);
  useEffect(() => { restart(); }, [active]);

  const currentStage = stage >= 0 ? SCRIPT_STAGES[stage] : null;
  const stageProgress = currentStage?.progress || 0;

  return (
    <section id="interactive-demo" className="py-24 px-6 lg:px-8" data-testid="landing-interactive-demo">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4FF00]/25 bg-[#D4FF00]/[0.05] text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-5 font-mono">
            <Sparkles className="w-3 h-3" /> {lang === 'fr' ? 'Sans compte · Sans carte' : 'No account · No card'}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
            {lang === 'fr' ? 'Essaie HighlightAI en 15 secondes' : 'Try HighlightAI in 15 seconds'}
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            {lang === 'fr'
              ? 'Choisis un contenu, regarde l\'IA travailler, découvre le Coach, le Growth Dashboard et le Creator DNA.'
              : 'Pick a content type, watch the AI work, then explore the AI Coach, Growth Dashboard, and Creator DNA.'}
          </p>
        </div>

        {/* Persona picker */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="demo-persona-tabs">
          {Object.entries(DEMOS).map(([key, d]) => (
            <button
              key={key}
              onClick={() => setActive(key)}
              data-testid={`demo-persona-${key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold transition-colors ${
                active === key
                  ? 'text-black'
                  : 'text-zinc-400 border-white/10 bg-white/[0.02] hover:text-zinc-200 hover:border-white/25'
              }`}
              style={active === key ? { backgroundColor: ACC, borderColor: ACC } : {}}
            >
              <d.icon className="w-4 h-4" />
              {lang === 'fr' ? d.labelFr : d.label}
            </button>
          ))}
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="grid lg:grid-cols-[1.1fr_1fr] gap-6"
        >
          {/* Left panel: source + processing */}
          <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6" data-testid="demo-processing-panel">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Source</span>
              <span className="text-[10px] font-mono text-zinc-500">{demo.source}</span>
            </div>
            <div className="relative aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-zinc-800 to-zinc-950">
              <div className="absolute inset-0 flex items-center gap-[3px] px-4 py-4">
                {Array.from({ length: 40 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className="flex-1 rounded-sm"
                    style={{ background: stage >= 0 && i <= (stage / SCRIPT_STAGES.length) * 40 ? ACC : '#3f3f46' }}
                    animate={{ height: stage >= 0 ? [`${20 + (i % 5) * 12}%`, `${45 + (i % 7) * 8}%`, `${25 + (i % 3) * 15}%`] : '35%' }}
                    transition={{ duration: 1.2 + (i % 4) * 0.1, repeat: Infinity, delay: i * 0.02 }}
                  />
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                {stage === -1 && (
                  <button
                    onClick={start}
                    data-testid="demo-start-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4FF00] text-black font-bold text-sm shadow-lg hover:bg-[#B3D600] transition-colors"
                  >
                    <Play className="w-4 h-4 fill-black" />
                    {lang === 'fr' ? 'Lancer le traitement' : 'Start processing'}
                  </button>
                )}
              </div>
            </div>

            {/* Stage tracker */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-zinc-400" data-testid="demo-current-stage">
                  {currentStage
                    ? (lang === 'fr' ? currentStage.labelFr : currentStage.label)
                    : (lang === 'fr' ? 'En attente' : 'Idle')}
                </span>
                <span className="text-xs font-mono tabular-nums text-[#D4FF00]" data-testid="demo-progress">
                  <AnimatedNumber value={stageProgress} suffix="%" />
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  className="h-full bg-[#D4FF00]"
                  animate={{ width: `${stageProgress}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              {done && (
                <button
                  onClick={restart}
                  data-testid="demo-restart-btn"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-mono text-zinc-400 hover:text-[#D4FF00]"
                >
                  <RefreshCw className="w-3 h-3" /> {lang === 'fr' ? 'Rejouer' : 'Replay'}
                </button>
              )}
            </div>
          </div>

          {/* Right panel: reveal — clips + DNA + Coach + Growth */}
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="reveal"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-4"
                data-testid="demo-reveal"
              >
                {/* Growth mini-KPIs */}
                <div className="grid grid-cols-4 gap-2" data-testid="demo-growth-strip">
                  <MiniKPI label="Clips" value={demo.growth.clips} testid="demo-kpi-clips" />
                  <MiniKPI label="Publiés" value={demo.growth.published} testid="demo-kpi-pub" />
                  <MiniKPI label="Views" value={demo.growth.views} compact testid="demo-kpi-views" />
                  <MiniKPI label="AI Score" value={demo.growth.avgScore} testid="demo-kpi-score" />
                </div>

                {/* AI Coach recommendation */}
                <div className="rounded-xl border border-[#D4FF00]/25 bg-[#D4FF00]/[0.04] p-4 flex gap-3" data-testid="demo-coach">
                  <div className="w-9 h-9 rounded-lg bg-[#D4FF00]/15 flex items-center justify-center shrink-0">
                    <Brain className="w-4 h-4 text-[#D4FF00]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-[#D4FF00] mb-1">AI Coach</div>
                    <p className="text-sm text-zinc-100 leading-relaxed">{demo.coachTip}</p>
                  </div>
                </div>

                {/* Creator DNA mini-radar */}
                <MiniDNA dna={demo.dna} />

                {/* Generated clips */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
                    {lang === 'fr' ? 'Clips générés' : 'Generated clips'}
                  </div>
                  <div className="grid grid-cols-3 gap-2" data-testid="demo-clips">
                    {demo.clips.map((c, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.08, duration: 0.3 }}
                        className="relative aspect-[9/16] rounded-lg overflow-hidden border border-white/10"
                        style={{ background: `linear-gradient(135deg, hsl(${i * 90 + 30}, 40%, 20%), #0a0a0a)` }}
                      >
                        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${ACC}25`, color: ACC, border: `1px solid ${ACC}66` }}>
                          {c.score}
                        </div>
                        <div className="absolute bottom-1.5 right-1.5 text-[9px] font-mono text-white/70 bg-black/60 px-1 rounded">{c.dur}s</div>
                        <div className="absolute inset-x-1 bottom-6">
                          <div className="tiktok-caption text-[9px] leading-tight">{c.title.slice(0, 20).toUpperCase()}</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <Link to="/register" className="block" data-testid="demo-signup-cta">
                  <div className="mt-3 rounded-xl bg-[#D4FF00] text-black text-center py-3 font-bold text-sm hover:bg-[#B3D600] transition-colors flex items-center justify-center gap-2">
                    {lang === 'fr' ? 'Créer mon compte gratuit' : 'Create my free account'} <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              </motion.div>
            ) : (
              <motion.div
                key="waiting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="rounded-2xl border border-white/6 bg-white/[0.015] p-6 flex flex-col items-center justify-center text-center"
                data-testid="demo-waiting"
              >
                <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-zinc-500" />
                </div>
                <p className="text-sm text-zinc-400 max-w-xs">
                  {stage === -1
                    ? (lang === 'fr' ? 'Lance le traitement pour voir l\'IA travailler.' : 'Start processing to see the AI work.')
                    : (lang === 'fr' ? 'L\'IA analyse ton contenu…' : 'AI is analyzing your content…')}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

const MiniKPI = ({ label, value, compact = false, testid }) => (
  <div className="p-3 rounded-xl border border-white/8 bg-white/[0.02] text-center" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500 mb-1">{label}</div>
    <AnimatedNumber value={value} format={compact ? 'compact' : 'default'} className="font-heading text-xl font-black" />
  </div>
);

// Compact 5-axis radar — mirrors the app's CreatorDNARadar but sized for the section.
const MiniDNA = ({ dna }) => {
  const size = 200;
  const cx = size / 2, cy = size / 2, R = 70;
  const axes = [
    { key: 'hook',       label: 'Hook',       v: dna.hook },
    { key: 'retention',  label: 'Ret.',       v: dna.retention },
    { key: 'emotion',    label: 'Emo.',       v: dna.emotion },
    { key: 'virality',   label: 'Viral',      v: dna.virality },
    { key: 'publishing', label: 'Pub.',       v: dna.publishing },
  ];
  const pts = axes.map((a, i) => {
    const ang = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
    const r = (a.v / 100) * R;
    return { x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang), label: a.label, ang };
  });
  return (
    <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4 flex items-center gap-4" data-testid="demo-mini-dna">
      <div className="shrink-0">
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
          {[0.4, 0.7, 1].map((r, i) => (
            <polygon
              key={i}
              points={Array.from({ length: 5 }).map((_, j) => {
                const ang = -Math.PI / 2 + (j * 2 * Math.PI) / 5;
                const rr = R * r;
                return `${cx + rr * Math.cos(ang)},${cy + rr * Math.sin(ang)}`;
              }).join(' ')}
              fill="none" stroke="#ffffff12"
            />
          ))}
          <motion.polygon
            points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
            fill={`${ACC}22`} stroke={ACC} strokeWidth={2}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{ transformOrigin: `${cx}px ${cy}px` }}
          />
          {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3} fill={ACC} />)}
        </svg>
      </div>
      <div className="text-xs">
        <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">Creator DNA</div>
        <ul className="space-y-1 font-mono tabular-nums">
          {axes.map((a) => (
            <li key={a.key} className="flex items-center gap-2">
              <span className="w-10 text-zinc-500">{a.label}</span>
              <span className="text-zinc-200 font-bold">{a.v}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default InteractiveDemo;
